// Miniatura "referencia directa" de una notificación (estilo 02). Resuelve la
// imagen del ENTITY al que apunta la notif SIN tocar dónde se crean: del link
// /vehicle/:id saca la foto del carro, de /product/:id la del producto, y del
// senderId el avatar del taller/mecánico/cliente. Si no hay, el render cae al
// tile de color con el ícono del tipo.
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import {
  Calendar, Star, Wrench, Bell, AlertTriangle, Fuel, MessageCircle, Package, Siren,
} from 'lucide-react';
import { snapshotMigrationConfig } from './migrationConfig';
import { fetchPublicProfile } from '../api/users';
import { fetchProduct } from '../api/catalog';

const PURPLE = '#8B5CF6';

// Ícono + color por tipo (compartido por Inbox y la campana para no divergir).
export function notifTypeMeta(type, tokens) {
  switch (type) {
    case 'appointment': return { Icon: Calendar, color: tokens.amber };
    case 'review':      return { Icon: Star, color: PURPLE };
    case 'service':     return { Icon: Wrench, color: tokens.green };
    case 'reminder':    return { Icon: Bell, color: tokens.blue };
    case 'alert':       return { Icon: AlertTriangle, color: tokens.amber };
    case 'fuel':        return { Icon: Fuel, color: tokens.blue };
    case 'message':     return { Icon: MessageCircle, color: tokens.blue };
    case 'order':       return { Icon: Package, color: tokens.green };
    case 'emergency':   return { Icon: Siren, color: tokens.red };
    default:            return { Icon: Bell, color: tokens.textDim };
  }
}

// ¿A qué entidad apunta la notif? (para la miniatura)
export function notifRef(n) {
  const link = n.link || '';
  let m;
  if ((m = link.match(/^\/vehicle\/([^/?#]+)/))) return { kind: 'vehicle', id: m[1] };
  if ((m = link.match(/^\/product\/([^/?#]+)/))) return { kind: 'product', id: m[1] };
  if (n.senderId) return { kind: 'sender', id: n.senderId };
  return null;
}

// Resuelve las miniaturas de una lista de notifs (lecturas batched + deduplicadas).
// Degrada en silencio ante permiso/inexistencia. Devuelve { [notifId]: url }.
//
// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║ ⚠️ UNA MINIATURA NO PUEDE TUMBAR LA LISTA DE NOTIFICACIONES.              ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
//
// Esto lo llaman el buzón y la campana. Todo lo de aquí dentro es adorno: si no
// se resuelve una imagen, el render cae al tile de color con el ícono del tipo y
// el usuario no pierde ni un aviso. Por eso CADA lectura va en su propio
// `try/catch` y ni una sola excepción sube — incluida la del interruptor, que se
// lee dentro de un `catch` propio para que un fallo al consultarlo deje el
// camino viejo en vez de dejar la lista sin miniaturas.
export async function resolveNotifThumbs(notifs) {
  const vehicleIds = new Set(); const productIds = new Set(); const senderIds = new Set();
  for (const n of notifs) {
    if (n.image) continue;
    const r = notifRef(n);
    if (!r) continue;
    if (r.kind === 'vehicle') vehicleIds.add(r.id);
    else if (r.kind === 'product') productIds.add(r.id);
    else senderIds.add(r.id);
  }
  const grab = (coll, ids) => Promise.all([...ids].map(async (id) => {
    try { const s = await getDoc(doc(db, coll, id)); return [id, s.exists() ? s.data() : null]; }
    catch { return [id, null]; }
  }));

  // `users` y `catalog` tienen interruptor cableado aquí. El carro sigue siendo
  // `vehicles`, que esta tanda no toca: se queda donde está y su miniatura se
  // pierde sola si su base cambia, que es exactamente lo mismo que pasa hoy
  // cuando las reglas deniegan la lectura.
  //
  // Las dos fuentes salen de la MISMA instantánea: preguntar dos veces podría
  // devolver respuestas distintas si el interruptor se toca en medio, y aquí no
  // hay nada que ganar con eso.
  let fuenteUsers = 'firestore';
  let fuenteCatalog = 'firestore';
  try {
    const snap = await snapshotMigrationConfig();
    fuenteUsers = snap.readsFrom('users');
    fuenteCatalog = snap.readsFrom('catalog');
  } catch { /* el camino viejo */ }

  // La ficha pública trae `photoURL`, que es el único campo que se usa abajo.
  const grabSenders = () => Promise.all([...senderIds].map(async (id) => {
    try { return [id, await fetchPublicProfile(id)]; }
    catch { return [id, null]; }
  }));

  // La ficha del producto trae `photos[]` ya adaptado, que es lo único que se usa
  // abajo. `fetchProduct` devuelve `null` en 404 —producto borrado o restringido—
  // y aquí eso es lo correcto: una notificación vieja que apunta a algo que ya no
  // está se queda con su tile de color, no con un error.
  const grabProducts = () => Promise.all([...productIds].map(async (id) => {
    try { return [id, await fetchProduct(id)]; }
    catch { return [id, null]; }
  }));

  const [veh, prod, usr] = await Promise.all([
    grab('vehicles', vehicleIds),
    fuenteCatalog === 'postgres' ? grabProducts() : grab('products', productIds),
    fuenteUsers === 'postgres' ? grabSenders() : grab('users', senderIds),
  ]);
  const vMap = new Map(veh); const pMap = new Map(prod); const uMap = new Map(usr);
  const out = {};
  for (const n of notifs) {
    if (n.image) { out[n.id] = n.image; continue; }
    const r = notifRef(n);
    if (!r) continue;
    let url = null;
    if (r.kind === 'vehicle') url = (vMap.get(r.id) || {}).photoURL || null;
    else if (r.kind === 'product') { const p = pMap.get(r.id); url = (p && p.photos && p.photos[0]) || null; }
    else url = (uMap.get(r.id) || {}).photoURL || null;
    if (url) out[n.id] = url;
  }
  return out;
}
